// components/industry-templates/TechEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Code, Zap, Award, ArrowRight, ExternalLink } from 'lucide-react';

export const TechEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Tech Stack Showcase */}
      {industryData?.tech_stack && (
        <Card className="border-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Code className="w-6 h-6" style={{ color: theme.primary }} />
              Tech Stack & Technologies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {industryData.tech_stack.map((tech: string, idx: number) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-blue-500/50 transition-all hover:scale-105"
                >
                  <div className="text-2xl mb-2">⚡</div>
                  <p className="text-white font-semibold text-sm">{tech}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keynote Speakers */}
      {industryData?.keynote_speakers && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-6 h-6" style={{ color: theme.primary }} />
              Keynote Speakers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.keynote_speakers.map((speaker: any, idx: number) => (
                <div
                  key={idx}
                  className="flex gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white"
                    style={{ backgroundColor: theme.primary }}
                  >
                    {speaker.name[0]}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white">{speaker.name}</h4>
                    <p className="text-sm text-slate-400">{speaker.title}</p>
                    <p className="text-xs text-slate-500 mt-1">{speaker.topic}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tracks/Sessions */}
      {industryData?.tracks && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="w-6 h-6" style={{ color: theme.primary }} />
              Conference Tracks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.tracks.map((track: any, idx: number) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg border-l-4 bg-white/5"
                  style={{ borderColor: theme.primary }}
                >
                  <h4 className="font-bold text-white mb-1">{track.name}</h4>
                  <p className="text-sm text-slate-400">{track.description}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge className="text-xs" style={{ backgroundColor: theme.primary }}>
                      {track.duration}
                    </Badge>
                    <Badge variant="outline" className="text-xs border-white/20 text-white">
                      {track.level}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hackathon Section */}
      {industryData?.hackathon && (
        <Card
          className="border-2 backdrop-blur overflow-hidden"
          style={{ borderColor: theme.primary, backgroundColor: 'rgba(99, 102, 241, 0.1)' }}
        >
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Hackathon Challenge
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="font-bold text-white text-xl mb-2">
                Prize Pool: {industryData.hackathon.prize_pool}
              </h4>
              <p className="text-slate-300">{industryData.hackathon.theme}</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <p className="text-2xl font-bold" style={{ color: theme.primary }}>
                  {industryData.hackathon.duration}
                </p>
                <p className="text-xs text-slate-400">Duration</p>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <p className="text-2xl font-bold" style={{ color: theme.primary }}>
                  {industryData.hackathon.max_team_size}
                </p>
                <p className="text-xs text-slate-400">Team Size</p>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <p className="text-2xl font-bold" style={{ color: theme.primary }}>
                  {industryData.hackathon.mentors_available}
                </p>
                <p className="text-xs text-slate-400">Mentors</p>
              </div>
            </div>
            <Button className="w-full" style={{ backgroundColor: theme.primary }}>
              Register for Hackathon <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Sponsors */}
      {industryData?.sponsors && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Sponsors & Partners</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
              {industryData.sponsors.map((sponsor: string, idx: number) => (
                <div
                  key={idx}
                  className="aspect-square flex items-center justify-center bg-white/5 rounded-lg border border-white/10 hover:border-blue-500/50 transition-all"
                >
                  <p className="text-white font-bold text-sm text-center">{sponsor}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
